import * as React from "react";
import { IndianRupee, Loader2 } from "lucide-react";
import {
  Button,
  Input,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import { ApiError } from "@eduviaos/shared";
import {
  PAYMENT_METHOD_LABELS,
  formatPaise,
  istToday,
  rupeesToPaise,
  useCreatePayment,
  usePreviewPayment,
  type AllocationMode,
  type PaymentPreview,
  type TeamPaymentMethod,
} from "@/lib/teamPay/api";
import { ErrorBanner, Field, NativeSelect, PreviewNote } from "@/components/team-pay/shared";
import { errorMessage } from "@/lib/teamPay/words";

/**
 * Phase SC-22d — record money handed to a team member. The BE previews
 * how it splits (salary oldest first → dues → excess as an advance); we
 * send the previewed `ledger_version` so a concurrent change yields 409
 * instead of a silently different split.
 */

const MODE_LABELS: Record<AllocationMode, string> = {
  auto: "Work it out",
  all_salary: "All towards pay",
  all_advance: "All as an advance",
};

const PREVIEW_DEBOUNCE_MS = 350;

export function PaymentSheet({
  open,
  onOpenChange,
  payeeId,
  memberName,
  owedPaise,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payeeId: string | null;
  memberName: string;
  owedPaise: number;
}) {
  const [amount, setAmount] = React.useState("");
  const [paidOn, setPaidOn] = React.useState(istToday());
  const [method, setMethod] = React.useState<TeamPaymentMethod>("cash");
  const [mode, setMode] = React.useState<AllocationMode>("auto");
  const [reference, setReference] = React.useState("");
  const [note, setNote] = React.useState("");
  const [preview, setPreview] = React.useState<PaymentPreview | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const previewMutation = usePreviewPayment();
  const create = useCreatePayment();
  const { mutateAsync: runPreview } = previewMutation;

  React.useEffect(() => {
    if (open) {
      setAmount(owedPaise > 0 ? String(owedPaise / 100) : "");
      setPaidOn(istToday());
      setMethod("cash");
      setMode("auto");
      setReference("");
      setNote("");
      setPreview(null);
      setError(null);
    }
  }, [open, owedPaise]);

  const amountPaise = rupeesToPaise(amount);

  React.useEffect(() => {
    if (!open || !payeeId || amountPaise === null || !paidOn) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    const handle = window.setTimeout(() => {
      runPreview({ staff_payee_id: payeeId, amount_paise: amountPaise, paid_on: paidOn, mode })
        .then((result) => {
          if (!cancelled) {
            setPreview(result);
            setError(null);
          }
        })
        .catch((err: unknown) => {
          if (!cancelled) setError(errorMessage(err, "Couldn't work out the split."));
        });
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [open, payeeId, amountPaise, paidOn, mode, runPreview]);

  const canSubmit =
    payeeId !== null && amountPaise !== null && preview !== null && !create.isPending;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || payeeId === null || amountPaise === null || preview === null) return;
    setError(null);
    try {
      await create.mutateAsync({
        staff_payee_id: payeeId,
        amount_paise: amountPaise,
        paid_on: paidOn,
        mode,
        method,
        reference: reference.trim() || null,
        note: note.trim() || null,
        expected_ledger_version: preview.ledger_version,
      });
      toast.success(`Recorded ${formatPaise(amountPaise)} for ${memberName}.`);
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setPreview(null);
        setError("Their pay changed while you were here. Check the new split and save again.");
        runPreview({ staff_payee_id: payeeId, amount_paise: amountPaise, paid_on: paidOn, mode })
          .then(setPreview)
          .catch(() => undefined);
        return;
      }
      setError(errorMessage(err, "Couldn't record the payment. Please retry."));
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[480px]"
        data-testid="team-payment-sheet"
      >
        <SheetHeader className="border-border/60 border-b px-6 pb-4 pt-6">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="bg-primary/10 text-primary mt-0.5 grid size-10 shrink-0 place-items-center rounded-lg"
            >
              <IndianRupee className="size-5" />
            </span>
            <div className="flex-1">
              <SheetTitle className="tracking-tight">Pay {memberName}</SheetTitle>
              <SheetDescription className="mt-1 text-xs">
                {owedPaise > 0
                  ? `${formatPaise(owedPaise)} is owed as of today.`
                  : "Nothing is owed right now. Anything you pay is kept as an advance."}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
            {error ? <ErrorBanner message={error} /> : null}

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Amount (₹)" htmlFor="tpp-amount" required>
                <Input
                  id="tpp-amount"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.01"
                  autoFocus
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </Field>
              <Field label="Paid on" htmlFor="tpp-date" required>
                <Input
                  id="tpp-date"
                  type="date"
                  value={paidOn}
                  max={istToday()}
                  onChange={(e) => setPaidOn(e.target.value)}
                />
              </Field>
            </div>

            <div
              role="radiogroup"
              aria-label="How to count this money"
              className="bg-muted/40 grid grid-cols-3 gap-1 rounded-lg p-1"
            >
              {(Object.keys(MODE_LABELS) as AllocationMode[]).map((m) => (
                <button
                  key={m}
                  type="button"
                  role="radio"
                  aria-checked={mode === m}
                  onClick={() => setMode(m)}
                  className={cn(
                    "rounded-md px-2 py-1.5 text-xs font-medium transition-[background-color,color,transform] duration-150 ease-out active:scale-[0.97]",
                    mode === m
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {MODE_LABELS[m]}
                </button>
              ))}
            </div>

            {preview ? (
              <PreviewNote
                tone={preview.advance_paise > 0 ? "advance" : "neutral"}
                busy={previewMutation.isPending}
              >
                <p data-testid="payment-preview-summary">{preview.summary}</p>
                {preview.lines.length > 1 ? (
                  <ul className="text-muted-foreground mt-2 space-y-1 text-xs">
                    {preview.lines.map((line, idx) => (
                      <li
                        key={`${line.target}-${line.year ?? ""}-${line.month ?? ""}-${idx}`}
                        className="flex justify-between gap-3"
                      >
                        <span>{line.label}</span>
                        <span className="tabular-nums">{formatPaise(line.amount_paise)}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </PreviewNote>
            ) : amountPaise !== null ? (
              <PreviewNote busy>Working out the split…</PreviewNote>
            ) : null}

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Paid by" htmlFor="tpp-method">
                <NativeSelect
                  id="tpp-method"
                  value={method}
                  onChange={(e) => setMethod(e.target.value as TeamPaymentMethod)}
                >
                  {(Object.keys(PAYMENT_METHOD_LABELS) as TeamPaymentMethod[]).map((m) => (
                    <option key={m} value={m}>
                      {PAYMENT_METHOD_LABELS[m]}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Reference" htmlFor="tpp-ref">
                <Input
                  id="tpp-ref"
                  value={reference}
                  maxLength={120}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder="UPI ref, cheque no."
                />
              </Field>
            </div>
            <Field label="Note" htmlFor="tpp-note">
              <Input
                id="tpp-note"
                value={note}
                maxLength={500}
                onChange={(e) => setNote(e.target.value)}
              />
            </Field>
          </div>

          <div className="border-border/60 bg-card/40 flex items-center justify-end gap-2 border-t px-6 py-4 backdrop-blur">
            <SheetClose asChild>
              <Button variant="ghost" type="button" disabled={create.isPending}>
                Cancel
              </Button>
            </SheetClose>
            <Button type="submit" disabled={!canSubmit}>
              {create.isPending ? (
                <Loader2 aria-hidden="true" className="size-4 animate-spin" />
              ) : null}
              Record payment
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
